import { BybButton } from "@Components/common/byb-ds";
import { BybRewardSticker } from "@Components/common/byb-ds/byb-reward-sticker";
import ChallengeStatus from "@Enums/ChallengeStatus";
import RewardScarcity from "@Enums/RewardScarcity";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Box, Stack, Typography, keyframes, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BybDialog, DialogSection } from "../common";
import { glossEffect } from "../game-card";

// Define the animation keyframes
const slideGloss = keyframes`
  from {
    left: -175%;
  }
  to {
    left: 200%;
  }
`;

interface IChallengeDialog {
  status: ChallengeStatus;
  rewardName?: string;
  rewardScarcity?: RewardScarcity;
  rewardImageUrl?: string | null;
  open?: boolean;
  onContinue?: () => void;
  onClose?: () => void;
}

const ChallengeDialog = ({
  status,
  open = false,
  rewardName,
  rewardScarcity,
  rewardImageUrl,
  onContinue = () => {},
  onClose = () => {},
}: IChallengeDialog) => {
  const { i18n } = useI18n();
  const { t } = useTranslation("VR");
  const { globalState } = useGlobalContext();
  const theme = useTheme();
  const [completeModalOpen, setCompleteModalOpen] = useState(false);

  const handleClose = () => {
    setCompleteModalOpen(false);
    onClose?.();
  };

  const handleContinue = () => {
    handleClose();
    onContinue?.();
  };

  useEffect(() => {
    setCompleteModalOpen(open);
  }, [open]);

  return (
    <BybDialog
      name="challenge-dialog"
      open={completeModalOpen}
      onClose={!globalState ? handleClose : undefined} // prevent users in Bootcamp from closing the modal with a backdrop click
      transparent
      dialogActions={
        <BybButton color="primary" size="large" onClick={handleContinue} fullWidth>
          {i18n("continue")}
        </BybButton>
      }
    >
      <Stack spacing={2}>
        <DialogSection dark>
          <Stack
            spacing={2}
            sx={{
              height: "100%",
              textAlign: "center",
              padding: theme.spacing(2),
              zIndex: 1,
              alignItems: "center",
              color: theme.palette.dsNeutral.v300,
            }}
          >
            {rewardScarcity && (
              <Box
                sx={{
                  "> *": {
                    position: "relative",
                    overflow: "hidden",
                    // Add other styles for your element here
                    "&::before": {
                      ...glossEffect,
                      animation: `${slideGloss} 3s cubic-bezier(0.18, 0.9, 0.58, 1) infinite`,
                    },
                    border: "none",
                    boxShadow: !rewardImageUrl ? "0px 0px 10px rgba(0, 0, 0, 0.5)" : "none",
                  },
                }}
              >
                {!rewardImageUrl ? (
                  <BybRewardSticker scarcity={rewardScarcity} />
                ) : (
                  <img
                    src={rewardImageUrl}
                    alt={rewardName}
                    style={{
                      backgroundColor: "transparent",
                      width: theme.spacing(10),
                    }}
                  />
                )}
              </Box>
            )}
            <Typography variant="text6xl700" color="dsBase.v200">
              {i18n(`challenges.challenge.finished.modal.title.${status}`)}
            </Typography>
            <Typography variant="text2xl500" color="dsNeutral.v300">
              {i18n(`challenges.challenge.finished.modal.body.${status}`, {
                reward: rewardName ? t(rewardName) : "",
              })}
            </Typography>
          </Stack>
        </DialogSection>
      </Stack>
    </BybDialog>
  );
};

export default ChallengeDialog;
